import { Component } from '@theme/component';
import {
  getIntersectionRoot,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';
import { isLowPowerDevice, prefersReducedMotion } from '@theme/utilities';

/**
 * @param {number} value
 * @param {number} [min]
 * @param {number} [max]
 */
const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

/**
 * @param {number} value
 * @param {number} start
 * @param {number} end
 */
const phase = (value, start, end) => clamp((value - start) / (end - start));

/**
 * Native-scroll camera controller for the continuous Pop Crispies Gooey Trail.
 * It observes Ritual's actual scroll container and only writes local CSS values.
 */
class GooeyTrail extends Component {
  requiredRefs = ['story', 'stage', 'landmarkLabel'];

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  /** @type {EventTarget | undefined} */
  #scrollTarget;

  /** @type {number | undefined} */
  #frame;

  /** @type {number} */
  #lastWidth = window.innerWidth;

  /** @type {boolean} */
  #isVisible = true;

  /** @type {HTMLElement[]} */
  #waypoints = [];

  /** @type {string} */
  #activeKey = '';

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    this.#teardown();
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#teardown();
    this.#setup();
  }

  #setup() {
    delete this.dataset.enhanced;
    delete this.dataset.static;
    delete this.dataset.inView;
    delete this.dataset.hasScrolled;
    this.dataset.landmark = 'trailhead';
    this.#activeKey = '';
    this.style.setProperty('--gt-progress', '0');
    this.style.setProperty('--gt-mountain-entry', '0');
    this.style.setProperty('--gt-valley-climb', '0');
    this.style.setProperty('--gt-trail-progress', '0');
    this.#waypoints = Array.from(this.querySelectorAll('.gt-waypoint'));
    if (prefersReducedMotion() || isLowPowerDevice() || window.Shopify?.designMode) {
      this.dataset.static = 'true';
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#scrollTarget = getScrollEventTarget();
    this.dataset.enhanced = 'true';

    this.#scrollTarget.addEventListener('scroll', this.#requestUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#handleResize, { passive: true, signal });
    window.addEventListener('orientationchange', this.#requestUpdate, { passive: true, signal });
    scrollContainerMediaQuery.addEventListener('change', this.#handleScrollerChange, { signal });

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.#isVisible = entry?.isIntersecting ?? true;
        this.toggleAttribute('data-in-view', this.#isVisible);
        if (this.#isVisible) this.#requestUpdate();
      },
      { root: getIntersectionRoot(), rootMargin: '120% 0px' }
    );

    const story = this.refs.story;
    if (story instanceof Element) this.#intersectionObserver.observe(story);

    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#requestUpdate();
    });

    this.#update();
  }

  #teardown() {
    this.#controller?.abort();
    this.#controller = undefined;
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = undefined;
    this.#scrollTarget = undefined;
    if (this.#frame !== undefined) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }

  #requestUpdate = () => {
    if (!this.#isVisible || this.#frame !== undefined) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  };

  #handleResize = () => {
    const nextWidth = window.innerWidth;
    if (Math.abs(nextWidth - this.#lastWidth) < 24) return;
    this.#lastWidth = nextWidth;
    this.#requestUpdate();
  };

  #handleScrollerChange = () => {
    this.#teardown();
    queueMicrotask(() => {
      if (this.isConnected) this.#setup();
    });
  };

  #update() {
    const story = this.refs.story;
    if (!(story instanceof HTMLElement)) return;

    const rect = story.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const travel = Math.max(1, story.offsetHeight - viewportHeight);
    const progress = clamp(-rect.top / travel);
    const trailProgress = phase(progress, 0, 0.96);

    this.toggleAttribute('data-has-scrolled', progress > 0.002);

    const pull = this.#waypoints.find((waypoint) => waypoint.dataset.key === 'pull')
      ?? this.#waypoints.find((waypoint) => waypoint.dataset.key !== 'trailhead');
    const soft = this.#waypoints.find((waypoint) => waypoint.dataset.key === 'soft');
    const pullRect = pull?.getBoundingClientRect();
    const softRect = soft?.getBoundingClientRect();
    const mountainEntry = pullRect ? clamp((viewportHeight - pullRect.top) / (viewportHeight * 0.86)) : 0;
    let valleyClimb = 0;

    if (pullRect && softRect) {
      const startLine = viewportHeight * 0.72;
      const endLine = viewportHeight * 0.28;
      const climbDistance = softRect.top + softRect.height * 0.35 - pullRect.top;
      valleyClimb = clamp((startLine - pullRect.top) / Math.max(1, climbDistance + startLine - endLine));
    }

    this.style.setProperty('--gt-progress', progress.toFixed(4));
    this.style.setProperty('--gt-mountain-entry', mountainEntry.toFixed(4));
    this.style.setProperty('--gt-valley-climb', valleyClimb.toFixed(4));
    this.style.setProperty('--gt-trail-progress', trailProgress.toFixed(4));

    const road = this.#waypoints.find((waypoint) => waypoint.dataset.key === 'road');
    if (road) {
      const noteBottom = soft?.querySelector('.gt-note')?.getBoundingClientRect().bottom ?? 0;
      const roadRect = road.getBoundingClientRect();
      this.dispatchEvent(new CustomEvent('gt:road-update', {
        detail: { active: roadRect.top < viewportHeight * 0.65 && noteBottom < viewportHeight * 0.45 && roadRect.bottom > 0 },
      }));
    }

    const viewportCenter = viewportHeight * 0.52;
    let activeWaypoint = this.#waypoints[0];
    let shortestDistance = Number.POSITIVE_INFINITY;

    for (const waypoint of this.#waypoints) {
      const waypointRect = waypoint.getBoundingClientRect();
      const waypointCenter = waypointRect.top + waypointRect.height / 2;
      const distance = Math.abs(waypointCenter - viewportCenter);
      if (distance < shortestDistance) {
        shortestDistance = distance;
        activeWaypoint = waypoint;
      }
    }

    const activeKey = activeWaypoint?.dataset.key ?? 'trailhead';
    if (activeKey === this.#activeKey) return;

    this.#activeKey = activeKey;
    this.dataset.landmark = activeKey;
    const label = activeWaypoint?.dataset.label ?? 'Trailhead';
    const landmarkLabel = this.refs.landmarkLabel;
    if (landmarkLabel instanceof Element) landmarkLabel.textContent = label;
  }

}

if (!customElements.get('gooey-trail')) {
  customElements.define('gooey-trail', GooeyTrail);
}
